import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getStoredTokens,
  clearTokens,
  decodeToken,
  isTokenExpired,
  getLogoutUrl,
  type AuthTokens,
  type UserInfo,
} from './authService';
import { createAuthenticatedClient } from '@/lib/graphql';
import { VIEWER_FEATURES_QUERY, type ViewerFeaturesData } from '@/features/queries';

/** Maximum number of retry attempts for fetching viewer role */
const MAX_ROLE_RETRIES = 3;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  accessToken: string | null;
  isAdmin: boolean;
  roleError: string | null;
  setTokens: (tokens: AuthTokens) => Promise<void>;
  logout: () => void;
  retryFetchRole: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  // Note: We use ID token for API calls (not access token) because ID tokens contain the email claim
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const currentTokenRef = useRef<string | null>(null);

  const fetchViewerRole = useCallback(async (token: string, retryCount = 0): Promise<void> => {
    try {
      setRoleError(null);
      const client = createAuthenticatedClient(token);
      const data = await client.request<ViewerFeaturesData>(VIEWER_FEATURES_QUERY);
      if (data.viewer) {
        setIsAdmin(data.viewer.isAdmin);
        setUser((prev) => prev ? { ...prev, role: data.viewer?.role, isAdmin: data.viewer?.isAdmin } : null);
      }
    } catch (error) {
      console.error('Failed to fetch viewer role:', error);

      if (retryCount < MAX_ROLE_RETRIES) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = 2 ** retryCount * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        await fetchViewerRole(token, retryCount + 1);
      } else {
        setRoleError('Unable to verify admin status. Please refresh the page or try again.');
        // Don't set isAdmin to false - leave it in its previous state
      }
    }
  }, []);

  const retryFetchRole = useCallback(() => {
    if (currentTokenRef.current) {
      fetchViewerRole(currentTokenRef.current, 0);
    }
  }, [fetchViewerRole]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Check for stored tokens on mount
      const tokens = getStoredTokens();
      // Use ID token for API calls - it contains the email claim needed by the backend
      if (tokens && !isTokenExpired(tokens.idToken)) {
        setAccessToken(tokens.idToken);
        setUser(decodeToken(tokens.idToken));
        setIsAuthenticated(true);
        // Store token for retry functionality
        currentTokenRef.current = tokens.idToken;
        // Fetch role from viewer query and wait for it to complete
        await fetchViewerRole(tokens.idToken);
      } else if (tokens) {
        // Tokens exist but are expired
        clearTokens();
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, [fetchViewerRole]);

  const handleSetTokens = async (tokens: AuthTokens) => {
    // Use ID token for API calls - it contains the email claim needed by the backend
    setAccessToken(tokens.idToken);
    setUser(decodeToken(tokens.idToken));
    setIsAuthenticated(true);
    // Store token for retry functionality
    currentTokenRef.current = tokens.idToken;
    // Show loading while fetching role
    setIsLoading(true);
    // Fetch role from viewer query and wait for it to complete
    await fetchViewerRole(tokens.idToken);
    setIsLoading(false);
  };

  const handleLogout = () => {
    clearTokens();
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setRoleError(null);
    currentTokenRef.current = null;
    window.location.href = getLogoutUrl();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        accessToken,
        isAdmin,
        roleError,
        setTokens: handleSetTokens,
        logout: handleLogout,
        retryFetchRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
